import { useEffect, useState, useCallback, useRef } from "react";
import { cartomancerApi } from "./api";

/**
 * Cloud Sync hook for Cartomancer Travel Logs (Premium feature).
 *
 * Responsibilities:
 *  - Mirror server logs to localStorage per project (offline read cache).
 *  - Auto-poll server every 45s while Premium and online.
 *  - Detect drift between local mirror and server (missing/new/updated logs).
 *  - Auto-adopt server state (server is source of truth).
 *  - Surface a "conflict" state briefly when local mirror had logs that the
 *    server no longer has (e.g. deleted from another device/tab) — surfaced
 *    to the user via the badge so they know why entries vanished.
 *
 * States: idle | syncing | synced | offline | conflict | error
 *
 * The hook is a passive observer: it does NOT own the logs list (that lives
 * in the parent EditorPage). Instead, when it detects server drift it calls
 * `applyServerLogs(nextLogs)` to update the parent state atomically.
 */

const AUTO_SYNC_MS = 45_000;

function mirrorKey(projectId, userId) {
  return `cartomancer_logs_mirror_${userId || "anon"}_${projectId}`;
}

function readMirror(projectId, userId) {
  try {
    const raw = localStorage.getItem(mirrorKey(projectId, userId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeMirror(projectId, userId, logs) {
  try {
    // Store compact mirror: id + updated_at + title for offline preview
    const compact = (logs || []).map((l) => ({
      id: l.id,
      title: l.title,
      updated_at: l.updated_at || l.created_at || "",
      created_at: l.created_at || "",
    }));
    const payload = { logs: compact, saved_at: new Date().toISOString() };
    localStorage.setItem(mirrorKey(projectId, userId), JSON.stringify(payload));
  } catch {
    // localStorage may be full or disabled — silently ignore
  }
}

function diffMirror(mirror, serverLogs) {
  const mirrorIds = new Set((mirror?.logs || []).map((l) => l.id));
  const serverIds = new Set(serverLogs.map((l) => l.id));
  const serverById = new Map(serverLogs.map((l) => [l.id, l]));
  const onlyLocal = [...mirrorIds].filter((id) => !serverIds.has(id));
  const onlyServer = [...serverIds].filter((id) => !mirrorIds.has(id));
  const updated = [];
  for (const local of mirror?.logs || []) {
    const srv = serverById.get(local.id);
    if (srv && srv.updated_at && local.updated_at && srv.updated_at !== local.updated_at) {
      updated.push(local.id);
    }
  }
  return { onlyLocal, onlyServer, updated };
}

export default function useCloudSync({
  projectId,
  userId,
  isPremium,
  logs,               // current in-memory logs (owned by parent)
  applyServerLogs,    // (nextLogs) => void, called when we detect drift
}) {
  const [state, setState] = useState("idle");
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [driftInfo, setDriftInfo] = useState(null); // {onlyLocal, onlyServer, updated} when state === "conflict"
  const [errorMsg, setErrorMsg] = useState("");

  const timerRef = useRef(null);
  const inFlight = useRef(false);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  // Load mirror on mount / project change so the UI has something to show
  // even before the first sync completes.
  useEffect(() => {
    if (!projectId) return;
    const mirror = readMirror(projectId, userId);
    if (mirror?.saved_at) setLastSyncedAt(mirror.saved_at);
  }, [projectId, userId]);

  // Persist mirror whenever parent logs change (so offline caches stay warm)
  useEffect(() => {
    if (!projectId || !isPremium || !Array.isArray(logs)) return;
    writeMirror(projectId, userId, logs);
  }, [logs, projectId, userId, isPremium]);

  const syncNow = useCallback(async () => {
    if (!projectId || !isPremium) return;
    if (inFlight.current) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setState("offline");
      return;
    }
    inFlight.current = true;
    setState("syncing");
    setErrorMsg("");
    try {
      const resp = await cartomancerApi.listLogs(projectId);
      const serverLogs = Array.isArray(resp) ? resp : (resp?.logs || []);
      const mirror = readMirror(projectId, userId);
      const diff = diffMirror(mirror, serverLogs);
      const nowIso = new Date().toISOString();

      // Adopt server state unconditionally (server is source of truth)
      // Only call applyServerLogs when different from what parent already has.
      const parentIds = new Set((logs || []).map((l) => l.id));
      const serverIds = new Set(serverLogs.map((l) => l.id));
      const differsFromParent =
        parentIds.size !== serverIds.size ||
        [...serverIds].some((id) => !parentIds.has(id)) ||
        serverLogs.some((sl) => {
          const cur = (logs || []).find((l) => l.id === sl.id);
          return cur && cur.updated_at !== sl.updated_at;
        });
      if (differsFromParent) applyServerLogs(serverLogs);

      writeMirror(projectId, userId, serverLogs);
      setLastSyncedAt(nowIso);

      // Surface a "conflict" state briefly if server lost logs we had locally
      // (indicates deletion from another device/tab)
      if (diff.onlyLocal.length > 0) {
        setDriftInfo(diff);
        setState("conflict");
      } else {
        setDriftInfo(null);
        setState("synced");
      }
    } catch (err) {
      const msg = String(err?.message || err || "");
      // Distinguish offline from real error
      if (msg.match(/failed to fetch|networkerror|network request failed/i) ||
          (typeof navigator !== "undefined" && navigator.onLine === false)) {
        setState("offline");
      } else {
        setErrorMsg(msg || "Errore sconosciuto");
        setState("error");
      }
    } finally {
      inFlight.current = false;
    }
  }, [projectId, userId, isPremium, logs, applyServerLogs]);

  // Acknowledge a conflict — clear the drift banner
  const acknowledgeConflict = useCallback(() => {
    setDriftInfo(null);
    setState("synced");
  }, []);

  // Auto-sync loop (Premium only)
  useEffect(() => {
    if (!isPremium || !projectId) return undefined;
    // Initial sync shortly after mount to catch any drift from other devices
    const initial = setTimeout(syncNow, 800);
    timerRef.current = setInterval(syncNow, AUTO_SYNC_MS);
    return () => {
      clearTimeout(initial);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [isPremium, projectId, syncNow]);

  // Online/offline listeners
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onOnline = () => { if (isPremium) syncNow(); };
    const onOffline = () => setState("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [isPremium, syncNow]);

  return {
    state,
    lastSyncedAt,
    driftInfo,
    errorMsg,
    syncNow,
    acknowledgeConflict,
  };
}
